import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import useCodex from '../../hooks/useCodex';
import useChapters from '../../hooks/useChapters';
import useCodexEvents from '../../hooks/useCodexEvents';
import useDisclosure from '../../hooks/useDisclosure';
import { paths } from '../../routes/paths';
import { chaptersApi } from '../../lib/api';
import { buildRawTextPDF } from '../../lib/pdf';
import { downloadDecoupageCSV } from '../../lib/decoupage';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Icon from '../../components/ui/Icon/Icon';
import ChapterList from '../../components/chapter/ChapterList/ChapterList';
import ChapterEditor from '../../components/chapter/ChapterEditor/ChapterEditor';
import SceneIndex from '../../components/chapter/SceneIndex/SceneIndex';
import CreateChapterModal from '../../components/chapter/CreateChapterModal/CreateChapterModal';
import ExportTypesModal from '../../components/export/ExportTypesModal/ExportTypesModal';
import styles from './CodexPage.module.css';

export default function CodexPage() {
  const { workspaceId, codexId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { codex, loading: codexLoading } = useCodex(codexId);
  const { chapters, loading, error, createChapter, refetch, patchChapter, deleteChapter, reorderChapters, applyChapterPatch } =
    useChapters(codexId);

  // Realtime codex events:
  //  - chapters_changed: structural (created) -> refetch the tree
  //  - chapter_patched: title changed -> patch local state (seamless, no flicker)
  useCodexEvents(codexId, (msg) => {
    if (msg?.type === 'chapter_patched') {
      applyChapterPatch(msg.id, { title: msg.title });
    } else if (msg?.type === 'chapters_changed') {
      refetch();
    }
  });

  const createModal = useDisclosure();
  const exportTypesModal = useDisclosure();
  const [selectedId, setSelectedId] = useState(null);
  const [createParentId, setCreateParentId] = useState(null);
  const [treeOpen, setTreeOpen] = useState(true);
  const [chapterContents, setChapterContents] = useState({});
  const [exporting, setExporting] = useState(false);
  // Imperative bridge: SceneIndex calls into the editor to scroll to the Nth
  // scene heading. ChapterEditor publishes the jump fn when it mounts.
  const sceneJumpRef = useRef(null);

  const isScreenplay = codex?.type === 'screenplay';
  const isPlainScreenplay = isScreenplay && codex?.subtype === 'plain';
  const chapterIdsKey = useMemo(() => chapters.map((c) => c.id).join(','), [chapters]);

  // plain screenplays are 1-to-1 with a single chapter. The WorkspacePage
  // bootstraps it at creation time, but if the user lands on an empty plain
  // codex (older codex, failed seed, manual delete) we lazily seed it here
  // using the codex title so the editor opens straight away. Guarded against
  // double-fires by tracking the codex id we last seeded.
  const [seedingFor, setSeedingFor] = useState(null);
  useEffect(() => {
    if (!isPlainScreenplay) return;
    if (loading || codexLoading) return;
    if (chapters.length > 0) return;
    if (seedingFor === codexId) return;
    setSeedingFor(codexId);
    createChapter(codex.title || 'Untitled').catch((err) => {
      console.warn('failed to seed plain chapter', err);
      setSeedingFor(null);
    });
  }, [isPlainScreenplay, loading, codexLoading, chapters.length, codexId, codex?.title, createChapter, seedingFor]);

  // Unique uppercase character names across all screenplay chapters. Drives
  // autocomplete in CharacterPickerModal and the Dialog block flow.
  const knownCharacters = useMemo(() => {
    if (!isScreenplay) return [];
    const set = new Set();
    const prefixRe = /^[\t ]*@@CHR\s+(.+?)\s*$/gim;
    const inlineRe = /<char>\s*([^<]+?)\s*<\/char>/gi;
    for (const id of Object.keys(chapterContents)) {
      const body = chapterContents[id] || '';
      prefixRe.lastIndex = 0;
      let m;
      while ((m = prefixRe.exec(body)) !== null) {
        const n = m[1].trim().toUpperCase();
        if (n) set.add(n);
      }
      inlineRe.lastIndex = 0;
      while ((m = inlineRe.exec(body)) !== null) {
        const n = m[1].trim().toUpperCase();
        if (n) set.add(n);
      }
    }
    return Array.from(set).sort();
  }, [isScreenplay, chapterContents]);

  // Fetch all chapter bodies for screenplay codices. Used by the ChapterList
  // expandable rows (to show characters per scene) and by the decoupage export.
  useEffect(() => {
    if (!isScreenplay) {
      setChapterContents({});
      return undefined;
    }
    const ids = chapterIdsKey ? chapterIdsKey.split(',') : [];
    if (ids.length === 0) {
      setChapterContents({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        chaptersApi
          .getContent(id)
          .then((r) => [id, r?.content || ''])
          .catch(() => [id, '']),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setChapterContents(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [isScreenplay, chapterIdsKey]);

  // Keep a valid selection as chapters load/change. Honors `?chapter=<id>`
  // so internal chapter-ref links jump directly to the target.
  useEffect(() => {
    if (chapters.length === 0) {
      setSelectedId(null);
      return;
    }
    const wanted = searchParams.get('chapter');
    if (wanted && chapters.some((c) => c.id === wanted)) {
      if (selectedId !== wanted) setSelectedId(wanted);
      setSearchParams({}, { replace: true });
      return;
    }
    if (!chapters.some((c) => c.id === selectedId)) {
      setSelectedId(chapters[0].id);
    }
  }, [chapters, selectedId, searchParams, setSearchParams]);

  const selected = chapters.find((c) => c.id === selectedId) || null;
  const title = codex?.title || (codexLoading ? 'loading...' : 'codex');

  const openCreate = (parentId) => {
    setCreateParentId(parentId ?? null);
    createModal.open();
  };

  const handleCreate = async (chapterTitle, { description = '' } = {}) => {
    const id = await createChapter(chapterTitle, { parentId: createParentId, description });
    setSelectedId(id);
  };

  // Yield two animation frames so React can commit and the browser can paint
  // the "Preparing..." view before the synchronous PDF builder blocks the main
  // thread. Without this the modal appears frozen until the download starts.
  const yieldToPaint = () =>
    new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );

  const handleSelectExport = async (type) => {
    if (type === 'rawtext' || type === 'rawdraft') {
      setExporting(true);
      try {
        await yieldToPaint();
        const visible = chapters.filter((ch) => !ch.hidden);
        // Prefer the locally-cached chapter bodies (the editor keeps them in
        // sync on every keystroke). The S3 snapshot only catches up after the
        // hub's flush tick (10s), so refetching here can hand back stale text
        // when the user exports immediately after typing.
        let contents = chapterContents;
        const missing = visible.filter((ch) => !(ch.id in contents));
        if (missing.length > 0) {
          const fetched = await Promise.all(
            missing.map((ch) =>
              chaptersApi.getContent(ch.id).then((r) => [ch.id, r?.content || '']).catch(() => [ch.id, '']),
            ),
          );
          contents = { ...contents, ...Object.fromEntries(fetched) };
        }
        const withContent = visible.map((ch) => ({
          title: ch.title,
          content: contents[ch.id] || '',
        }));
        buildRawTextPDF(title, withContent, { codexType: codex?.type });
      } catch (err) {
        console.error('rawtext export failed', err);
      } finally {
        setExporting(false);
        exportTypesModal.close();
      }
      return;
    }
    if (type === 'decoupage') {
      setExporting(true);
      try {
        await yieldToPaint();
        const visible = chapters.filter((ch) => !ch.hidden);
        // Reuse already-cached contents when available; fall back to a fresh fetch.
        let contents = chapterContents;
        const missing = visible.filter((ch) => !(ch.id in contents));
        if (missing.length > 0) {
          const fetched = await Promise.all(
            missing.map((ch) =>
              chaptersApi.getContent(ch.id).then((r) => [ch.id, r?.content || '']).catch(() => [ch.id, '']),
            ),
          );
          contents = { ...contents, ...Object.fromEntries(fetched) };
        }
        downloadDecoupageCSV(title, visible, contents);
      } catch (err) {
        console.error('decoupage export failed', err);
      } finally {
        setExporting(false);
        exportTypesModal.close();
      }
    }
  };

  return (
    <>
      <Link to={paths.workspace(workspaceId)} className={styles.back}>
        back to workspace
      </Link>

      <PageHeader
        kicker="codex"
        title={title}
        subtitle="// write chapters, then export"
        actions={
          <>
            <Button
              variant="ghost"
              leftIcon={<Icon name={treeOpen ? 'PanelRightClose' : 'PanelRightOpen'} size={14} />}
              onClick={() => setTreeOpen((v) => !v)}
            >
              {treeOpen
                ? (isPlainScreenplay ? 'Hide scenes' : 'Hide tree')
                : (isPlainScreenplay ? 'Show scenes' : 'Show tree')}
            </Button>
            <Button
              leftIcon={<Icon name="Download" size={14} tone="default" />}
              onClick={exportTypesModal.open}
              disabled={chapters.length === 0}
            >
              Export
            </Button>
          </>
        }
      />

      {loading && <p className={styles.loading}>loading chapters</p>}
      {!loading && error && (
        <EmptyState icon="AlertTriangle" title="Could not load chapters" description={error} />
      )}

      {!loading && !error && (
        <div className={`${styles.layout} ${treeOpen ? '' : styles.collapsed}`}>
          <div className={styles.main}>
            {selected ? (
              <ChapterEditor
                chapter={selected}
                workspaceId={workspaceId}
                codexId={codexId}
                chapters={chapters}
                codexType={codex?.type}
                codexSubtype={codex?.subtype}
                knownCharacters={knownCharacters}
                sceneJumpRef={isPlainScreenplay ? sceneJumpRef : undefined}
                onLocalContentChange={(id, md) => {
                  if (!id) return;
                  setChapterContents((prev) => ({ ...prev, [id]: md }));
                }}
              />
            ) : (
              <EmptyState
                icon="FileText"
                title={isScreenplay && !isPlainScreenplay ? 'No scenes in this draft yet' : 'No chapter yet'}
                description={
                  isPlainScreenplay
                    ? 'Setting up your screenplay…'
                    : isScreenplay
                      ? 'Create your first scene and start writing.'
                      : 'Create a chapter to start writing.'
                }
                action={
                  isPlainScreenplay ? null : (
                    <Button
                      leftIcon={<Icon name="Plus" size={14} />}
                      onClick={() => openCreate(null)}
                    >
                      {isScreenplay ? 'Create scene' : 'Create chapter'}
                    </Button>
                  )
                }
              />
            )}
          </div>

          {treeOpen && isPlainScreenplay && selected && (
            <SceneIndex
              content={chapterContents[selected.id] || ''}
              onJump={(i) => sceneJumpRef.current?.(i)}
            />
          )}

          {treeOpen && !isPlainScreenplay && (
            <ChapterList
              chapters={chapters}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onCreate={openCreate}
              onPatch={patchChapter}
              onDelete={async (id) => {
                await deleteChapter(id);
                if (selectedId === id) {
                  const remaining = chapters.filter((c) => c.id !== id);
                  setSelectedId(remaining[0]?.id ?? null);
                }
              }}
              onReorder={reorderChapters}
              codexType={codex?.type}
              chapterContents={chapterContents}
            />
          )}
        </div>
      )}

      <CreateChapterModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        onCreate={handleCreate}
        codexType={codex?.type}
      />
      <ExportTypesModal
        isOpen={exportTypesModal.isOpen}
        onClose={exportTypesModal.close}
        onSelect={handleSelectExport}
        codexType={codex?.type}
        codexSubtype={codex?.subtype}
        loading={exporting}
      />
    </>
  );
}
